import {Stack, Text} from '@mantine/core';
import assert from 'assert';
import React, {useEffect, useState} from 'react';

import PrimitiveValueEditor, {SupportedLeafType} from 'data-frames/PrimitiveValueEditor';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {tryParse} from 'notebook/data-tables/DatetimeFields';
import {ParameterFieldType} from 'samples/plate-maps/PlateMapConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DatetimeField from 'ui-library/inputs/DatetimeField';

import {ParameterLayerData} from '../parameter-layers/machine/ParameterLayerMachine';

/** A procedure parameter's TEXT/FLOAT field type maps to the PrimitiveValueEditor's leaf type. */
const PARAMETER_FIELD_TYPE_TO_LEAF_TYPE: Record<
  ParameterFieldType.TEXT | ParameterFieldType.FLOAT,
  SupportedLeafType
> = {
  [ParameterFieldType.TEXT]: LeafType.TEXT,
  [ParameterFieldType.FLOAT]: LeafType.DECIMAL,
};

export interface ParameterLayerSettingsProps {
  activeLayer: ParameterLayerData;
  pendingValue: string | number | null;
  hasSelectedWells: boolean;
  onUpdatePendingValue: (value: string | number | null) => void;
}

/**
 * Datetime value editor that keeps a local display string. DatetimeField fires onChange on every
 * keystroke (including invalid partials); only canonical values are propagated upward, so typing a
 * datetime isn't wiped mid-entry.
 */
function ParameterDatetimeEditor({
  pendingValue,
  hasSelectedWells,
  onUpdatePendingValue,
}: {
  pendingValue: string | null;
  hasSelectedWells: boolean;
  onUpdatePendingValue: (value: string | null) => void;
}): JSX.Element {
  const [displayValue, setDisplayValue] = useState<string | null>(pendingValue);
  useEffect(() => {
    setDisplayValue(pendingValue);
  }, [pendingValue]);

  return (
    <DatetimeField
      labelStyle={DatetimeField.LABEL_STYLES.HIDDEN}
      value={displayValue}
      onChange={(value) => {
        setDisplayValue(value);
        if (value === '') {
          onUpdatePendingValue(null);
          return;
        }
        const canonical = tryParse(value);
        // Store canonical ISO-8601 only once it parses; leave the prior value while still typing.
        if (canonical != null) {
          onUpdatePendingValue(canonical);
        }
      }}
      isDisabled={!hasSelectedWells}
    />
  );
}

/** Settings panel for a procedure-parameter layer: enter a TEXT/FLOAT/DATETIME value for selected wells. */
export default function ParameterLayerSettings({
  activeLayer,
  pendingValue,
  hasSelectedWells,
  onUpdatePendingValue,
}: ParameterLayerSettingsProps): JSX.Element {
  assert(activeLayer != null, 'Expected active layer to be defined.');

  return (
    <Stack spacing="sm" {...getTestProps({component: 'ParameterLayerSettings', element: 'root'})}>
      <Text size="sm" fw="bold">
        {activeLayer.name}
      </Text>
      <Text size="sm" c="dimmed">
        Select wells in the plate to assign a value for this parameter.
      </Text>
      {activeLayer.fieldType === ParameterFieldType.DATETIME ? (
        <ParameterDatetimeEditor
          pendingValue={typeof pendingValue === 'string' ? pendingValue : null}
          hasSelectedWells={hasSelectedWells}
          onUpdatePendingValue={onUpdatePendingValue}
        />
      ) : (
        <PrimitiveValueEditor
          leafType={PARAMETER_FIELD_TYPE_TO_LEAF_TYPE[activeLayer.fieldType]}
          value={pendingValue}
          onChange={(value) => {
            onUpdatePendingValue(typeof value === 'boolean' ? String(value) : value);
          }}
          disabled={!hasSelectedWells}
        />
      )}
      {!hasSelectedWells && (
        <Text size="xs" c="dimmed">
          Select one or more wells to assign a value.
        </Text>
      )}
    </Stack>
  );
}
